import type { IFetchAdGroupsDataProps } from '@bingads-webui-react/copilot-troubleshooting-data';
import { ISearchPhraseInfo, IAttachmentItem } from './types';

export const adGroupDataParams: NonNullable<IFetchAdGroupsDataProps['params']> = {
  $expand: 'Campaign($select=CampaignType,Id,Name)',
  $top: '100',
  $skip: '0',
  $count: 'true',
  $orderby: 'DeliveryStatus asc',
  $nullifynotapplybidamounts: 'true',
  $select: 'Name,EntityStatus,Id',
};

export const SearchCharacterList = ['/', '@'];

/**
 * Removes the search phrase from the text and returns the updated text and the cursor position
 * where the entity should be inserted.
 * @param {string} text The full text content of the node.
 * @param {string} searchPhrase The search phrase to remove (e.g., "/ele").
 * @returns {Object|undefined} The updated text and cursor position, or undefined if the search phrase was not found.
 */
export const removeSearchPhraseFromText = (text: string, searchPhrase: string): { updatedText: string; insertPosition: number } | undefined => {
  const index = text.indexOf(searchPhrase);
  if (index === -1) {
    return undefined;
  }
  return {
    updatedText: text.slice(0, index) + text.slice(index + searchPhrase.length),
    insertPosition: index,
  };
};

/**
 * Extracts the search phrase from the given text at the specified offset, using provided symbols.
 * @param {string} text The input text to search within.
 * @param {number} offset The position in the text to check for a search phrase.
 * @param {string} nodeKey The key associated with the node.
 * @param {string[]} [symbols] The list of symbols that indicate endpoints.
 * @returns {ISearchPhraseInfo | undefined} An ISearchPhraseInfo object if conditions are met; otherwise, undefined.
 */
export const getTripleSpaceSearchPhrase = (text: string, offset: number, nodeKey: string, symbols: string[] = SearchCharacterList): ISearchPhraseInfo | undefined => {
  if (offset > text.length || offset < 0) {
    return undefined;
  }

  // If the index is in the middle of a word, return undefined
  if (offset < text.length && text[offset] !== ' ') {
    return undefined;
  }

  // Count spaces and find word boundaries
  let spaceCount = 0;
  let start = offset;
  let hasEndpoint = false;

  while (start > 0 && spaceCount < 4) {
    const currentChar = text[start - 1];

    // Check for endpoint characters
    if (symbols.includes(currentChar)) {
      start -= 1;
      hasEndpoint = true;
      break;
    }

    if (currentChar === ' ') {
      spaceCount += 1;
    }
    start -= 1;
  }

  // Return undefined if more than 3 spaces
  if (spaceCount > 3) {
    return undefined;
  }

  // return undefined if the word does not start with a space
  if (start !== 0 && text[start - 1] !== ' ') {
    return undefined;
  }

  const word = text.slice(start, offset);

  // Return the phrase if it contains an endpoint character or starts with a symbol
  if (hasEndpoint || symbols.some(symbol => word.startsWith(symbol))) {
    return {
      nodeKey,
      searchPhrase: word,
    };
  }

  return undefined;
};

export function getEntitiesFilteredBySearchPhrase(entities: any, searchPhrase?: string) {
  if (!searchPhrase || searchPhrase.length < 1 || SearchCharacterList.includes(searchPhrase)) {
    return entities;
  }

  const filterEntity = (entity: any) => {
    const searchLower = searchPhrase.toLowerCase();
    return (
      entity.name.toLowerCase().includes(searchLower)
      || entity.primaryAttribute?.toLowerCase().includes(searchLower)
      || entity.secondaryAttribute?.toLowerCase().includes(searchLower)
    );
  };

  const filteredCampaigns = entities.campaigns.filter(filterEntity);
  const filteredAdGroups = entities.adGroups.filter(filterEntity);

  return {
    ...entities,
    campaigns: filteredCampaigns,
    adGroups: filteredAdGroups,
    all: [...filteredCampaigns, ...filteredAdGroups],
  };
}

export function getFormattedMessage(message: string, selectedEntities: IAttachmentItem[], isPageContextEnabled?: boolean) {
  // Process the value to include formatted entities
  let formattedMessage = message.trim();
  // De-duplicate entities by ID (if ID-based) or name (legacy)
  const dedupeKey = isPageContextEnabled ? 'id' : 'name';
  const uniqueEntities = Array.from(new Map<string, IAttachmentItem>(selectedEntities.map(entity => [entity[dedupeKey] as string, entity])).values());

  // Sort de-duplicated entities by name length in descending order
  const sortedEntities = uniqueEntities.sort((a, b) =>
    (b.name?.length || 0) - (a.name?.length || 0));

  sortedEntities.forEach((entity) => {
    if (entity.name && entity.id && entity.category) {
      // Use word boundaries and escape all special regex characters
      const escapedName = entity.name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
      // Create regex that matches the exact entity name
      const regex = new RegExp(escapedName, 'g');
      // When pilot is ON: JSON object, when OFF: {Category:Name}
      const replacement = isPageContextEnabled
        ? JSON.stringify({ entityType: entity.category, entityId: entity.id, entityName: entity.name })
        : `{${entity.category}:${entity.name}}`;
      formattedMessage = formattedMessage.replace(regex, replacement);
    }
  });
  return formattedMessage;
}

export const TabNames = {
  all: _TL_('All'),
  campaigns: _TL_('Campaigns'),
  adGroups: _TL_('Ad Groups'),
};

export const EntitiesErrorLabels = {
  all: _TL_('campaigns nor ad groups'),
  campaigns: _TL_('campaigns'),
  adGroups: _TL_('ad groups'),
};

export const SearchPlaceholders = {
  all: _TL_('Search'),
  campaigns: _TL_('Search campaign name or type'),
  adGroups: _TL_('Search ad group'),
};

export const CampaignTypes = {
  Default: _TL_('Search'),
  Lodging: _TL_('Lodging'),
  Shopping: _TL_('Shopping'),
  DynamicSearchAds: _TL_('Dynamic Search Ads'),
  Audience: _TL_('Audience'),
  Smart: _TL_('Smart'),
  Hotel: _TL_('Hotel'),
  PerformanceMax: _TL_('Performance Max'),
  App: _TL_('App'),
  LinkedIn: _TL_('LinkedIn'),
};
